import { Send, X } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import {
  contactSchema,
  validateContactPayload
} from "../validation/contact";
import type { ContactErrors } from "../validation/contact";
import { SectionHeading } from "./SectionHeading";

type Status = "idle" | "sending" | "sent" | "error";

export function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<ContactErrors>({});
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  useEffect(() => {
    if (!showSuccessToast) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowSuccessToast(false);
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [showSuccessToast]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = Object.fromEntries(formData.entries());
    const validation = validateContactPayload(payload);

    setErrors(validation.errors);
    setMessage("");
    setShowSuccessToast(false);

    if (!validation.valid) {
      setStatus("error");
      setMessage("Please fix the highlighted fields before sending.");
      return;
    }

    setStatus("sending");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(validation.data)
      });

      if (!response.ok) {
        const serverError = await response.json().catch(() => null);
        const errorMessage =
          typeof serverError?.error === "string"
            ? serverError.error
            : "Unable to send inquiry.";

        if (serverError?.errors) {
          setErrors(serverError.errors);
          setMessage(errorMessage);
          setStatus("error");
          return;
        }

        throw new Error(errorMessage);
      }

      form.reset();
      setErrors({});
      setStatus("sent");
      setMessage("Inquiry received. We will follow up with a practical next step.");
      setShowSuccessToast(true);
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Unable to send inquiry.");
    }
  }

  return (
    <>
      <form className="contact-form" noValidate onSubmit={handleSubmit}>
        <div>
          <SectionHeading eyebrow="Project Inquiry" title="Send the scope." />
          <p>Share drawings, specifications, materials, schedule, and finish requirements. We'll respond with a practical next step.</p>
        </div>
        <div className="form-grid">
        <label>
          Name
          <input
            aria-describedby={errors.name ? "name-error" : undefined}
            aria-invalid={Boolean(errors.name)}
            maxLength={contactSchema.fields.name.maxLength}
            minLength={contactSchema.fields.name.minLength}
            name="name"
            autoComplete="name"
            required
          />
          {errors.name ? <span className="field-error" id="name-error">{errors.name}</span> : null}
        </label>
        <label>
          Email
          <input
            aria-describedby={errors.email ? "email-error" : undefined}
            aria-invalid={Boolean(errors.email)}
            maxLength={contactSchema.fields.email.maxLength}
            name="email"
            type="email"
            autoComplete="email"
            required
          />
          {errors.email ? (
            <span className="field-error" id="email-error">{errors.email}</span>
          ) : null}
        </label>
      </div>
      <div className="form-grid">
        <label>
          Company
          <input
            aria-describedby={errors.company ? "company-error" : undefined}
            aria-invalid={Boolean(errors.company)}
            maxLength={contactSchema.fields.company.maxLength}
            name="company"
            autoComplete="organization"
          />
          {errors.company ? (
            <span className="field-error" id="company-error">{errors.company}</span>
          ) : null}
        </label>
        <label>
          Phone
          <input
            aria-describedby={errors.phone ? "phone-error" : undefined}
            aria-invalid={Boolean(errors.phone)}
            maxLength={contactSchema.fields.phone.maxLength}
            name="phone"
            type="tel"
            autoComplete="tel"
          />
          {errors.phone ? (
            <span className="field-error" id="phone-error">{errors.phone}</span>
          ) : null}
        </label>
      </div>
      <label>
        Project Type
        <select
          aria-describedby={errors.projectType ? "project-type-error" : undefined}
          aria-invalid={Boolean(errors.projectType)}
          name="projectType"
          defaultValue="custom-fabrication"
          required
        >
          <option value="custom-fabrication">Custom fabrication</option>
          <option value="products">Product or component</option>
          <option value="finishing">Finishing or powder coating</option>
          <option value="design-assist">Design assist</option>
        </select>
        {errors.projectType ? (
          <span className="field-error" id="project-type-error">{errors.projectType}</span>
        ) : null}
      </label>
      <label>
        Scope Notes
        <textarea
          aria-describedby={errors.message ? "message-error" : undefined}
          aria-invalid={Boolean(errors.message)}
          maxLength={contactSchema.fields.message.maxLength}
          minLength={contactSchema.fields.message.minLength}
          name="message"
          rows={6}
          required
        />
        {errors.message ? (
          <span className="field-error" id="message-error">{errors.message}</span>
        ) : null}
      </label>
      <div className="form-bottom">
        <p aria-live="polite">{message}</p>
        <button className="button primary" disabled={status === "sending"} type="submit">
          {status === "sending" ? "Sending..." : "Send Inquiry"}
          <Send size={18} />
        </button>
      </div>
      </form>
      {showSuccessToast ? (
        <div className="success-toast" role="status" aria-live="polite">
          <div>
            <strong>Inquiry sent.</strong>
            <p>Thank you. We will follow up with a practical next step.</p>
          </div>
          <button
            aria-label="Dismiss confirmation"
            type="button"
            onClick={() => setShowSuccessToast(false)}
          >
            <X size={16} />
          </button>
        </div>
      ) : null}
    </>
  );
}
